<script setup lang="ts">
import type { IListEventVaultQueryParams, IPossibleIncludes } from "@/api"
import type { IHostDashboardFilters } from "@/components/HostDashboardFilters/types"
import type { UnredeemedPackage } from "@/models"
import type { ToastHtmlElement } from "@/types"
import { useInfiniteQuery, useQuery, useQueryClient } from "@tanstack/vue-query"
import { useHead } from "@unhead/vue"
import { useUrlSearchParams } from "@vueuse/core"
import { computed, onMounted, reactive, ref, useTemplateRef, watchEffect } from "vue"
import { listEventVaults, listUnredeemedPackages, useGetEventVault } from "@/api"
import {
  CloseEventModal,
  DeleteEventModal,
  EventDeleteDrawer,
  EventExtendDrawer,
  EventLinksDrawer,
  HostDashboardFilters,
  HostEventCard,
  HostEventsTable,
  InstallBanner,
  PackageSubscriptionCard,
  RedeemPackagesDrawer,
  StartEventModal,
  ZipExportDrawer,
} from "@/components"
import {
  Alert,
  Button,
  ButtonGroup,
  ButtonLink,
  Card,
  DropdownMenu,
  Icon,
  NoData,
  Pagination,
  Toast,
} from "@/components/ui"
import { useModal, usePagination, useTranslation } from "@/composables"
import { SidebarLayout } from "@/layouts"
import { useAuthStore } from "@/stores/authStore"
import {
  DeleteEventModalKey,
  EventDeleteDrawerKey,
  EventExtendDrawerKey,
  EventLinksDrawerKey,
  HostDashboardFiltersKey,
  RedeemPackagesDrawerKey,
  ZipExportDrawerKey,
} from "@/symbols"
import { getCreateEventCTA, ViewMode } from "@/utils"

definePage({
  meta: {
    requiresAuth: true,
  },
})

const authStore = useAuthStore()

const pageCount = ref(1)
const selectedEventId = ref<number | undefined>()
const include = ref<IPossibleIncludes[]>(["zip", "template", "settings"])
const mode = ref<ViewMode>(ViewMode.CARD)

const messageToast = useTemplateRef<ToastHtmlElement | null>("messageToast")
const hostDashboardWrapperRef = useTemplateRef<HTMLElement | null>("hostDashboardWrapperRef")

const filters = reactive<IHostDashboardFilters>({
  perPage: 10,
})

const pagination = usePagination({
  initialPerPage: filters.perPage,
  pageCount,
  scrollStartRef: hostDashboardWrapperRef,
})
const queryClient = useQueryClient()
const { t } = useTranslation()

useHead({
  title: t("host_dashboard.page_title"),
})

const urlSearchParams = useUrlSearchParams<{ extend?: unknown, delete?: unknown, message?: unknown, hostAccountId?: unknown, export?: unknown, links?: unknown, redeem?: unknown }>()

const { toggle: toggleLinksDrawer } = useModal(EventLinksDrawerKey)
const { toggle: toggleExportDrawer } = useModal(ZipExportDrawerKey)
const { toggle: toggleExtendDrawer } = useModal(EventExtendDrawerKey)
const { toggle: toggleDeleteDrawer } = useModal(EventDeleteDrawerKey)
const { toggle: toggleConfirmDelete } = useModal(DeleteEventModalKey)
const { toggle: toggleFilters } = useModal(HostDashboardFiltersKey)
const { toggle: toggleRedeemPackages } = useModal(RedeemPackagesDrawerKey)

onMounted(() => {
  if (urlSearchParams.message && typeof urlSearchParams.message === "string") {
    messageToast.value?.showToast({
      title: urlSearchParams.message,
      icon: "CircleInfo",
    })
  }
})

const { data: eventVaults, isLoading, isError: isListEventVaultsError, isRefetching: isRefetchingListEventVaults, refetch: refetchListEventVaults } = useQuery({
  queryKey: ["listEventVaults", pagination.page, pagination.perPage, filters],
  queryFn: async () => {
    const options: IListEventVaultQueryParams = { page: pagination.page.value, include: include.value, ...filters }

    if (urlSearchParams.hostAccountId && typeof urlSearchParams.hostAccountId === "string") {
      const hostAccountId = Number.parseInt(urlSearchParams.hostAccountId)
      options.hostAccountId = Number.isNaN(hostAccountId) ? undefined : hostAccountId
    }

    const response = await listEventVaults(options)

    if (response.links?.Last) {
      pageCount.value = Number.parseInt(response.links.Last)
    }

    return response.eventVaults
  },
})

const { data: eventVault, isLoading: isLoadingEventVault, refetch: refetchEventVault, isFetching: isFetchingEventVault } = useGetEventVault(selectedEventId, { include: ["zip", "website"] })

const {
  data: packages,
  fetchNextPage: fetchMoreUnredeemed,
  isFetching: isFetchingUnredeemed,
  hasNextPage,
  isLoading: isLoadingPackages,
} = useInfiniteQuery({
  queryKey: ["listUnredeemedPackages"],
  queryFn: async (pageParams) => {
    const page = pageParams.pageParam ?? 1
    const queryParams = { page, perPage: 20, orderByDesc: false }

    const hostAccountId = typeof urlSearchParams.hostAccountId === "string" ? Number(urlSearchParams.hostAccountId) : undefined

    return !Number.isNaN(hostAccountId)
      ? listUnredeemedPackages(queryParams, hostAccountId)
      : listUnredeemedPackages(queryParams)
  },
  getNextPageParam: (lastPage) => {
    if (!lastPage.links || lastPage.links?.Current === lastPage.links?.Last) {
      return undefined
    }

    const nextPage = Number(lastPage.links?.Next)

    return Number.isNaN(nextPage) ? undefined : nextPage
  },
  initialPageParam: 1,
})

const createEventCTA = computed(() => getCreateEventCTA(authStore.user))
const hasSubscription = computed(() => Boolean(authStore.user?.packageSubscription?.packageSubscriptionId))

const mobileHeaderActions = computed(() => {
  const primaryAction = createEventCTA.value.to
    ? {
        key: "primary-cta",
        label: t("host_dashboard.purchase_event"),
        to: createEventCTA.value.to,
        dismissOnInteract: true,
      }
    : {
        key: "primary-cta",
        label: t("host_dashboard.purchase_event"),
        href: createEventCTA.value.href,
        dismissOnInteract: true,
      }

  return [
    primaryAction,
    {
      key: "create-free-event",
      label: t("host_dashboard.free_event"),
      to: "/event/create",
      dismissOnInteract: true,
    },
  ]
})

const packageSubscription = computed(() => authStore.user?.packageSubscription)

const unredeemedPackages = computed<UnredeemedPackage[]>(() => {
  return packages.value?.pages.flatMap(p => p.packages) ?? []
})

const canFetchMoreUnredeemed = computed(() => !isLoadingPackages.value && Boolean(unredeemedPackages.value?.length) && hasNextPage?.value)

function setSelectedEvent(eventVaultId?: number) {
  selectedEventId.value = eventVaultId
  refetchEventVault()
}

function toggleDrawer(toggleFn: () => void, eventVaultId?: number) {
  setSelectedEvent(eventVaultId)
  toggleFn()
}

const showLinksDrawer = (eventVaultId?: number) => toggleDrawer(toggleLinksDrawer, eventVaultId)
const showExportDrawer = (eventVaultId?: number) => toggleDrawer(toggleExportDrawer, eventVaultId)
const showExtendDrawer = (eventVaultId?: number) => toggleDrawer(toggleExtendDrawer, eventVaultId)
const showDeleteDrawer = (eventVaultId?: number) => toggleDrawer(toggleDeleteDrawer, eventVaultId)
const showConfirmDeleteEvent = (eventVaultId?: number) => toggleDrawer(toggleConfirmDelete, eventVaultId)

async function onZipRequested() {
  refetchEventVault()
  await queryClient.invalidateQueries({ queryKey: ["listEventVaults"] })
}

function onToastHidden() {
  delete urlSearchParams.message
}

watchEffect(() => {
  if (urlSearchParams.extend && typeof urlSearchParams.extend === "string") {
    showExtendDrawer(Number(urlSearchParams.extend))
    delete urlSearchParams.extend
  }
})

watchEffect(() => {
  if (urlSearchParams.delete && typeof urlSearchParams.delete === "string") {
    showDeleteDrawer(Number(urlSearchParams.delete))
    delete urlSearchParams.delete
  }
})

watchEffect(() => {
  if (urlSearchParams.export && typeof urlSearchParams.export === "string") {
    showExportDrawer(Number(urlSearchParams.export))
    delete urlSearchParams.export
  }
})

watchEffect(() => {
  if (urlSearchParams.links && typeof urlSearchParams.links === "string") {
    showLinksDrawer(Number(urlSearchParams.links))
    delete urlSearchParams.links
  }
})

watchEffect(() => {
  if (urlSearchParams.redeem && typeof urlSearchParams.redeem === "string" && urlSearchParams.redeem === "true") {
    if (unredeemedPackages.value?.length) {
      toggleRedeemPackages()
      delete urlSearchParams.redeem
    }
  }
})

function onFiltersUpdate(updates: IHostDashboardFilters) {
  if (updates.perPage) {
    filters.perPage = updates.perPage
  }
  filters.sortBy = updates.sortBy
  filters.orderBy = updates.orderBy
  filters.status = updates.status
  filters.product = updates.product
}
</script>

<template>
  <InstallBanner />
  <SidebarLayout>
    <template #header>
      <div class="flex items-center justify-between gap-4">
        <div class="grow">
          {{ t("host_dashboard.heading") }}
        </div>
        <ButtonLink
          v-if="hasSubscription"
          data-test="subscription_event_cta_button"
          variant="primary"
          prefix-icon="CirclePlus"
          v-bind="createEventCTA.to ? { to: createEventCTA.to } : { href: createEventCTA.href }"
        >
          <span class="sr-only md:not-sr-only">{{ createEventCTA.label }}</span>
        </ButtonLink>
        <DropdownMenu v-else :items="mobileHeaderActions" align="end">
          <template #activator>
            <Button data-test="add_actions_button" variant="primary" prefix-icon="CirclePlus">
              <span class="sr-only md:not-sr-only">{{ t("host_dashboard.add_package") }}</span>
            </Button>
          </template>
        </DropdownMenu>
      </div>
    </template>

    <template #content>
      <div class="w-full overflow-hidden container mt-4">
        <Alert v-if="(packages?.pages?.[0]?.meta?.unredeemedCount ?? 0) > 0" :closable="false" class="mb-4">
          <div class="flex justify-between items-center gap-4">
            <div class="flex gap-2 items-center">
              <Icon class="shrink-0" icon="CircleInfo" />
              <p>
                {{ t('host_dashboard.unredeemed_message', {
                  count:
                    packages?.pages[0].meta.unredeemedCount,
                }) }}
              </p>
            </div>
            <Button @click="toggleRedeemPackages">
              {{ t('view') }}
            </Button>
          </div>
        </Alert>

        <PackageSubscriptionCard
          v-if="packageSubscription?.packageSubscriptionId"
          :package-subscription="packageSubscription"
          class="mb-6"
        />

        <div class="flex justify-between items-end mb-6">
          <div class="flex items-center justify-center gap-2">
            <Button variant="tertiary" @click="toggleFilters">
              <Icon icon="Filter" /><span class="sr-only md:not-sr-only">{{ t("filter") }} </span>
            </Button>
            <ButtonGroup>
              <Button
                data-test="card_view_button"
                variant="tertiary"
                :disabled="mode === ViewMode.CARD"
                @click="mode = ViewMode.CARD"
              >
                <Icon icon="Cards" />
                <span class="hidden md:block">{{ t("host_dashboard.card_view") }}</span>
                <span class="sr-only">{{ t("host_dashboard.select_card_view") }}</span>
              </Button>
              <Button
                data-test="table_view_button"
                variant="tertiary"
                :disabled="mode === ViewMode.TABLE"
                @click="mode = ViewMode.TABLE"
              >
                <Icon icon="Table" />
                <span class="hidden md:block">{{ t("host_dashboard.table_view") }}</span>
                <span class="sr-only">{{ t("host_dashboard.select_table_view") }}</span>
              </Button>
            </ButtonGroup>
          </div>
        </div>

        <div ref="hostDashboardWrapperRef">
          <transition name="gpx-fade" mode="out-in">
            <div v-if="isLoading" class="flex flex-col gap-10">
              <HostEventCard v-for="n in 3" :key="n" is-loading />
            </div>
            <div v-else-if="mode === ViewMode.CARD && eventVaults?.length" class="flex flex-col gap-6">
              <HostEventCard
                v-for="(event, index) in eventVaults"
                :key="index"
                :event-vault="event"
                @show-links="showLinksDrawer"
                @show-export="showExportDrawer"
                @show-extend="showExtendDrawer"
                @show-delete="showDeleteDrawer"
              />
              <Pagination
                class="mb-5"
                :current-page="pagination.page.value" :per-page="pagination.perPage.value" :page-count="pageCount"
                :can-next="pagination.canNext.value" :can-previous="pagination.canPrevious.value" @next="pagination.next" @previous="pagination.previous"
              />
            </div>
            <div v-else-if="mode === ViewMode.TABLE && eventVaults?.length" class="flex flex-col gap-10 mb-8">
              <HostEventsTable
                :items="eventVaults"
                :pagination="pagination"
                @pagination-next="pagination.next"
                @pagination-previous="pagination.previous"
                @show-links="showLinksDrawer"
                @show-export="showExportDrawer"
              />
            </div>
            <NoData
              v-else-if="isListEventVaultsError && !eventVaults?.length"
              :headline="t('host_dashboard.load_error.headline')"
              :content="t('host_dashboard.load_error.content')"
            >
              <template #cta>
                <Button
                  prefix-icon="RotateCcw"
                  data-test="host-dashboard-events-error-refresh"
                  :disabled="isRefetchingListEventVaults"
                  @click="refetchListEventVaults"
                >
                  {{ isRefetchingListEventVaults ? t('host_dashboard.load_error.refreshing_btn') : t('host_dashboard.load_error.refresh_btn') }}
                </Button>
              </template>
            </NoData>
            <div v-else>
              <Card :with-padding="false" class="px-4 md:px-6">
                <div class="mx-auto flex w-full max-w-4xl flex-col items-center gap-8 py-8 text-center md:gap-10 md:py-10">
                  <div class="max-w-3xl">
                    <h3 class="font-comfortaa text-2xl font-bold" data-test="no-data-headline">
                      {{ t("host_dashboard.no_data.headline") }}
                    </h3>
                  </div>

                  <div class="flex w-full flex-col gap-4 sm:w-auto sm:flex-row sm:justify-center">
                    <ButtonLink
                      v-if="hasSubscription"
                      prefix-icon="CirclePlus"
                      size="lg"
                      data-test="host-dashboard-empty-primary-cta"
                      v-bind="createEventCTA.to ? { to: createEventCTA.to } : { href: createEventCTA.href }"
                    >
                      {{ createEventCTA.label }}
                    </ButtonLink>
                    <ButtonLink
                      v-else
                      to="/event/create"
                      prefix-icon="CirclePlus"
                      size="lg"
                      data-test="host-dashboard-empty-primary-cta"
                    >
                      {{ t("host_dashboard.no_data.primary_button_label") }}
                    </ButtonLink>
                  </div>

                  <div class="grid w-full gap-5 text-left md:grid-cols-3 md:gap-6">
                    <Card variant="offwhite" border-radius="sm" :with-padding="false">
                      <div class="flex flex-col gap-2 p-4 md:gap-4 md:p-8">
                        <div class="hidden size-10 items-center justify-center rounded-full bg-white text-primary md:flex">
                          <Icon icon="CircleCheck" />
                        </div>
                        <h4 class="font-comfortaa text-base font-bold text-primary">
                          {{ t("host_dashboard.no_data.benefit_one_title") }}
                        </h4>
                        <p class="font-montserrat text-sm leading-5 text-gray-700 md:leading-6">
                          {{ t("host_dashboard.no_data.benefit_one_content") }}
                        </p>
                      </div>
                    </Card>

                    <Card variant="offwhite" border-radius="sm" :with-padding="false">
                      <div class="flex flex-col gap-2 p-4 md:gap-4 md:p-8">
                        <div class="hidden size-10 items-center justify-center rounded-full bg-white text-primary md:flex">
                          <Icon icon="ImageOn" />
                        </div>
                        <h4 class="font-comfortaa text-base font-bold text-primary">
                          {{ t("host_dashboard.no_data.benefit_two_title") }}
                        </h4>
                        <p class="font-montserrat text-sm leading-5 text-gray-700 md:leading-6">
                          {{ t("host_dashboard.no_data.benefit_two_content") }}
                        </p>
                      </div>
                    </Card>

                    <Card variant="offwhite" border-radius="sm" :with-padding="false">
                      <div class="flex flex-col gap-2 p-4 md:gap-4 md:p-8">
                        <div class="hidden size-10 items-center justify-center rounded-full bg-white text-primary md:flex">
                          <Icon icon="Video" />
                        </div>
                        <h4 class="font-comfortaa text-base font-bold text-primary">
                          {{ t("host_dashboard.no_data.benefit_three_title") }}
                        </h4>
                        <p class="font-montserrat text-sm leading-5 text-gray-700 md:leading-6">
                          {{ t("host_dashboard.no_data.benefit_three_content") }}
                        </p>
                      </div>
                    </Card>
                  </div>

                  <p class="max-w-2xl text-center font-montserrat text-sm leading-6 text-gray-600">
                    {{ t("host_dashboard.no_data.footer") }}
                  </p>
                </div>
              </Card>
            </div>
          </transition>
        </div>
      </div>
    </template>
  </SidebarLayout>

  <RedeemPackagesDrawer
    :packages="unredeemedPackages" :is-fetching="isFetchingUnredeemed || isLoadingPackages"
    :can-fetch-more="canFetchMoreUnredeemed" @fetch-more="fetchMoreUnredeemed"
  />
  <CloseEventModal :is-loading="isLoadingEventVault" :event-vault="eventVault" />
  <HostDashboardFilters :filters="filters" @update:filters="onFiltersUpdate" />
  <EventLinksDrawer :is-loading="isLoadingEventVault" :event-vault="eventVault" />
  <ZipExportDrawer :is-loading="isLoadingEventVault || isFetchingEventVault" :event-vault="eventVault" @zip-requested="onZipRequested" />
  <EventExtendDrawer :is-loading="isLoadingEventVault" :event-vault="eventVault" />
  <EventDeleteDrawer
    :is-loading="isLoadingEventVault" :event-vault="eventVault"
    @zip-requested="onZipRequested"
    @delete-event="showConfirmDeleteEvent"
  />
  <DeleteEventModal :event-vault="eventVault" />
  <StartEventModal :is-loading="isLoadingEventVault" :event-vault="eventVault" />
  <Toast ref="messageToast" @hidden="onToastHidden" />
</template>
